import { createGroup } from '@/api/groups'
import { fetchLeafletDetails } from '@/api/leaflets'
import {
  fetchLeafletsProcessingStatus,
  updateLeaflet,
  uploadLeafletCover,
  uploadLeafletFilesAsync
} from '@/api/repositories/leafletsRepository'
import { addLeafletToStore } from '@/api/repositories/shopsRepository'
import { scheduleEventsBatch } from '@/api/scheduledEvents'
import { uuidRegExp } from '@/application.constants'
import store from '@/store'
import { useLeafletCreateFlow } from '@/views/dashboard/components/AddEditLeafletDialog/composables/useLeafletCreateFlow'
import { useScheduledEvents } from '@/views/dashboard/components/AddEditLeafletDialog/composables/useScheduledEvents'
import type { LeafletForm } from '@/views/dashboard/types/LeafletForm'

import { CreationInputError } from './creationErrors'
import { type CreationTask, sameContext } from './creationTask'

function assertCurrentContext(task: CreationTask) {
  const { name, profile, token } = store.getters
  if (!token || !sameContext({ account: name, country: profile }, task.context))
    throw new CreationInputError('operationContextChanged')
}

export function createCreationApi() {
  const groups = new Map<string, Promise<string>>()
  const { prepareLeafletPayload } = useLeafletCreateFlow()
  const { createScheduledLongLinkPayload, createScheduledThumbnailLinkPayload } = useScheduledEvents()

  async function resolveGroup(task: CreationTask) {
    const name = task.tab.groupId
    if (!name || uuidRegExp.test(name)) return
    const key = `${task.context.country}:${task.context.account}:${name}`
    if (!groups.has(key)) {
      groups.set(
        key,
        createGroup({ name })
          .then(response => {
            if (response.status !== 201 || !response.data?.id) throw new Error('Group creation failed')
            return response.data.id as string
          })
          .catch(error => {
            groups.delete(key)
            throw error
          })
      )
    }
    task.tab.groupId = await groups.get(key)!
  }

  function payload(task: CreationTask): LeafletForm {
    return { ...prepareLeafletPayload(task.tab, task.platforms), id: task.leafletId } as unknown as LeafletForm
  }

  async function create(task: CreationTask) {
    await resolveGroup(task)
    assertCurrentContext(task)
    return addLeafletToStore(payload(task), task.store.id, task.tab.watermark)
  }

  async function find(task: CreationTask): Promise<string | null> {
    try {
      const { data } = await fetchLeafletDetails(task.tab.naturalId)
      // The backend also resolves historical natural IDs. Never adopt that other leaflet.
      return data.naturalId === task.tab.naturalId ? data.id : null
    } catch (error) {
      if ((error as { response?: { status?: number } }).response?.status === 404) return null
      throw error
    }
  }

  async function update(task: CreationTask) {
    await resolveGroup(task)
    assertCurrentContext(task)
    await updateLeaflet(payload(task), task.store.id, {
      'old-natural-id': task.originalNaturalId,
      'watermark': task.tab.watermark
    })
  }

  async function upload(task: CreationTask) {
    assertCurrentContext(task)
    const body = new FormData()
    body.append('uniqueId', task.tab.naturalId)
    task.tab.fileList.forEach(file => body.append('files', file.raw!, file.name))
    return uploadLeafletFilesAsync(body, task.leafletId!, task.tab.watermark)
  }

  async function cover(task: CreationTask) {
    assertCurrentContext(task)
    const body = new FormData()
    body.append('firstPage', task.tab.firstPageFile[0].raw!)
    const response = await uploadLeafletCover(body, task.leafletId!)
    if (response.status && response.status !== 204 && response.status !== 200) throw new Error('Cover upload failed')
  }

  async function schedule(task: CreationTask) {
    assertCurrentContext(task)
    const events = [
      createScheduledLongLinkPayload(task.tab.scheduledLongLink, task.leafletId!),
      createScheduledThumbnailLinkPayload(task.tab.scheduledThumbnailTrackingLink, task.leafletId!)
    ].filter(event => event !== null)
    // A past event may already have executed and disappeared from the backend.
    if (events.some(event => new Date(event.plannedTime).getTime() <= Date.now())) {
      throw new CreationInputError('scheduledTimePassed')
    }
    if (!events.length) return
    const response = await scheduleEventsBatch(events)
    if (response.status !== 200) throw new Error('Scheduling failed')
  }

  async function finish(task: CreationTask) {
    const { leafletPostCreationService } = await import(
      '@/views/scrapper/new-leaflets-search/use-cases/leaflet-post-creation-service'
    )
    assertCurrentContext(task)
    await leafletPostCreationService.executePostCreationActions(
      task.leafletId!,
      {
        isFromScrapper: task.isFromScrapper,
        dialogStatus: 'create',
        brochureId: task.brochureId,
        store: task.store,
        isStillActive: () => {
          assertCurrentContext(task)
          return true
        },
        leafletRequestsAmount: 1
      },
      { propagateErrors: true }
    )
  }

  return { create, find, update, upload, cover, schedule, finish, statuses: fetchLeafletsProcessingStatus }
}

export type CreationApi = ReturnType<typeof createCreationApi>
